import React, { useEffect, useState } from "react";
import "./App.css";
import NavbarOrganism from "./components/organisms/navbarorganism/NavbarOrganism";
import { Routes, Route } from "react-router-dom";
import CreateProject from "./components/pages/createproject/CreateProject";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthenticationResult, EventMessage, EventType, IPublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import SignInPage from "./components/pages/signinpage/signinPage";
import NavbarOrganismMobile from "./components/organisms/navbarorganismmobile/NavbarOrganismMobile";
import Projects from "./components/pages/projects/Projects";
import { groupAccess, loginRequest } from "./utilities/authConfig";
import { RouteGuard } from "./utilities/RouteGuard";
import { setGroupsInStorage } from "./utilities/StorageUtils";
import MyProjects from "./components/pages/projects/MyProjects";
import { ApiErrorHandler, isApiError, IApiError } from "./utilities/apiTypes";

type AppProps = {
  pca: IPublicClientApplication;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        if (failureCount >= 3) return false;
        let statusCode: number | undefined;
        if (isApiError(error)) {
          statusCode = error.statusCode;
        } else {
          const axiosLike = error as { response?: { status?: number }; request?: unknown };
          statusCode = axiosLike.response?.status;
          if (statusCode === undefined && axiosLike.request && !axiosLike.response) statusCode = 0;
        }
        if (statusCode === undefined) return false;
        return ApiErrorHandler.isRetryableError({ statusCode } as IApiError);
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 4 ** attemptIndex, 10000),
    },
  },
});

function App({ pca }: AppProps) {
  pca.initialize().then(() => {
    pca.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        setGroupsInStorage(account, account.idTokenClaims?.groups as string[] || []);
        pca.setActiveAccount(account);
      }
    });
  });

  const { accounts } = useMsal();
  interface IdTokenClaims {
    groups?: string[];
  }

  useEffect(() => {
    if (accounts.length > 0) {
      const activeAccount = pca.getActiveAccount() || pca.getAllAccounts()[0];

      const request = {
        ...loginRequest,
        account: activeAccount,
      };

      pca.acquireTokenSilent(request).then((response: AuthenticationResult) => {
        const idTokenClaims = response.idTokenClaims as IdTokenClaims;
        if (idTokenClaims.groups) {
          setGroupsInStorage(request.account, idTokenClaims.groups);
        }
      }).catch((error: any) => {
        if (error instanceof InteractionRequiredAuthError) {
          pca.acquireTokenRedirect(request);
        } else {
          console.error(error);
        }
      });
    }
  }, [pca, accounts]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <MsalProvider instance={pca}>
          <AuthenticatedTemplate>
            {isMobile ? <NavbarOrganismMobile /> : <NavbarOrganism />}
            <Routes>
              <Route path="/" element={
                <RouteGuard requiredGroups={[groupAccess.admin, groupAccess.arbetsledning, groupAccess.worker]}>
                  <Projects />
                </RouteGuard>
              } />
              <Route path="/createproject" element={
                <RouteGuard requiredGroups={[groupAccess.admin, groupAccess.arbetsledning]}>
                  <CreateProject />
                </RouteGuard>} />
              <Route path="/projects" element={
                <RouteGuard requiredGroups={[groupAccess.admin, groupAccess.arbetsledning, groupAccess.worker]}>
                  <Projects />
                </RouteGuard>} />
              <Route path="/myProjects" element={
                <RouteGuard requiredGroups={[groupAccess.admin, groupAccess.arbetsledning, groupAccess.worker]}>
                  <MyProjects />
                </RouteGuard>} />
              {/* <Route path="/handleAccounts" element={
                <RouteGuard requiredGroups={[groupAccess.admin]}>
                  <HandleAccounts />
                </RouteGuard>} />*/}
              <Route path="*" element={
                <h2>404: Sidan finns inte.</h2>
              } />
            </Routes>
          </AuthenticatedTemplate>

          <UnauthenticatedTemplate>
            <SignInPage />
          </UnauthenticatedTemplate>

        </MsalProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;